import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchJwtToken } from "../../actions/global";
import { ensureJwPlayerLibrary } from "../../utils/jwPlayerLibrary";

const JW_LIBRARY_PUBLIC = "public";
const JW_LIBRARY_PROTECTED = "protected";

const JwPlayer = (props) => {
  const {
    videoId,
    aspectratio = "16:9",
    controls = false,
    drm = false,
    autostart = false,
    shouldPlay = false,
    mask = false,
    isMuted = 1,
    onReady,
    sectionType,
    playerMode,
  } = props || {};

  const playerRef = useRef(null);
  const jwInstanceRef = useRef(null);
  const onReadyRef = useRef(onReady);
  const dispatch = useDispatch();
  const [hasFirstFrame, setHasFirstFrame] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isProtectedSection = sectionType === "MASTER";
  const libraryMode =
    playerMode === JW_LIBRARY_PUBLIC || playerMode === JW_LIBRARY_PROTECTED
      ? playerMode
      : isProtectedSection
        ? JW_LIBRARY_PROTECTED
        : JW_LIBRARY_PUBLIC;

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  //resetujemo firstFrame kada se promeni video
  useEffect(() => {
    setHasFirstFrame(false);
    setIsLoading(true);
  }, [videoId]);

  useEffect(() => {
    const player = jwInstanceRef.current;
    if (!player) return;

    try {
      player.setMute(Number(isMuted) === 1);
    } catch (e) {
      console.warn("JW setMute error", e);
    }
  }, [isMuted]);

  //PLAYER SETUP — library load + JWT token in parallel
  useEffect(() => {
    if (!videoId) return;
    if (!playerRef.current) return;
    if (jwInstanceRef.current) return;

    let cancelled = false;

    const setupPlayer = async () => {
      const baseUrl = `https://cdn.jwplayer.com/v2/media/${videoId}`;

      try {
        // Run library load and JWT token fetch in parallel
        const [, tokenData] = await Promise.all([
          ensureJwPlayerLibrary({ dispatch, mode: libraryMode, drm }),
          drm ? dispatch(fetchJwtToken(videoId)) : Promise.resolve(null),
        ]);

        if (cancelled) return;

        const playlistUrl =
          drm && tokenData?.token
            ? `${baseUrl}/drm/cnhUzcQ9?token=${tokenData.token}`
            : baseUrl;

        if (!window.jwplayer || !playerRef.current) return;

        const hasUserInteracted =
          sessionStorage.getItem("userInteracted") === "1";
        const shouldMute = hasUserInteracted
          ? sessionStorage.getItem("globalMute") !== "0"
          : true;

        const instance = window.jwplayer(playerRef.current).setup({
          playlist: playlistUrl,
          width: "100%",
          aspectratio: aspectratio,
          controls: controls,
          autostart: autostart ? "viewable" : false,
          mute: shouldMute,
        });

        jwInstanceRef.current = instance;

        instance.on("firstFrame", () => {
          setHasFirstFrame(true);
          setIsLoading(false);
          onReadyRef.current?.();
        });

        instance.on("error", () => {
          setIsLoading(false);
        });

        instance.on("mute", (e) => {
          const newValue = e.mute ? 1 : 0;

          // browser unlock
          sessionStorage.setItem("userInteracted", "1");

          // global state
          sessionStorage.setItem("globalMute", newValue);

          console.log("JW mute changed:", newValue);
        });
      } catch (error) {
        if (!cancelled) {
          console.error("JWPlayer setup error:", error);
          setIsLoading(false);
        }
      }
    };

    setupPlayer();

    // cleanup
    return () => {
      cancelled = true;
      if (jwInstanceRef.current) {
        try {
          jwInstanceRef.current.stop();
          jwInstanceRef.current.remove();
        } catch (err) {
          console.warn("JW cleanup error:", err);
        }
        jwInstanceRef.current = null;
      }
    };
  }, [videoId, dispatch, drm, aspectratio, controls, autostart, libraryMode]);

  //2PLAY/PAUSE LOGIKA
  useEffect(() => {
    const player = jwInstanceRef.current;
    if (!player) return;
    if (typeof shouldPlay !== "boolean") return;

    try {
      if (shouldPlay) {
        player.play();
      } else {
        player.pause(true);
      }
    } catch (e) {
      console.warn("JW shouldPlay control error", e);
    }
  }, [shouldPlay]);

  //RENDER
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {mask && <div className="reel-modal-mask" />}

      {isLoading && (
        <div
          className="jw-player-loading"
          data-testid="jw-player-loading"
          aria-label="Video loading"
        >
          <div className="jw-player-loading__spinner" />
        </div>
      )}

      {/* POSTER – fallback background */}
      {!hasFirstFrame && videoId && (
        <img
          src={`https://cdn.jwplayer.com/v2/media/${videoId}/poster.jpg`}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            inset: 0,
            opacity: hasFirstFrame ? 0 : 1,
            transition: "opacity 0.3s ease",
            zIndex: hasFirstFrame ? 0 : 2,
            borderRadius: "12px",
          }}
        />
      )}

      {/* PLAYER – uvek aktivan */}
      {videoId && (
        <div
          ref={playerRef}
          style={{
            position: "absolute",
            borderRadius: "12px",
            inset: 0,
            zIndex: 1,
            transition: "opacity 0.3s ease",
          }}
        />
      )}
    </div>
  );
};

export default JwPlayer;
